import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Shield, Zap, Activity } from "lucide-react";
import { scanUrl, getStats } from "../lib/api";
import VerdictMark from "../components/VerdictMark";

export default function Landing() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);

  const onScan = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await scanUrl(url.trim());
      setResult(r);
    } catch (err) {
      setError(err?.response?.data?.detail || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b-2 border-black overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-[1400px] mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black text-[10px] tracking-[0.3em] uppercase" data-testid="hero-tag">
              <Activity size={12} /> URL Threat Intelligence · Live
            </div>
            {stats && (
              <div className="mt-3 flex flex-wrap gap-3 text-[10px] tracking-[0.25em] uppercase font-bold" data-testid="live-stats">
                <span className="px-2 py-1 bg-black text-white">{stats.total} URLs scanned</span>
                <span className="px-2 py-1 bg-[#E53935] text-white">{stats.dangerous} threats blocked</span>
                <span className="px-2 py-1 bg-[#00B259] text-white">{stats.safe} verified safe</span>
              </div>
            )}
            <h1 className="mt-6 text-5xl md:text-7xl font-black tracking-tighter uppercase leading-[0.85]" style={{ fontFamily: "'Clash Display'" }}>
              Don't Trust.<br/>Verify Every<br/>
              <span className="bg-black text-white px-2">URL.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-700">
              WallGard is a Chrome extension and dashboard that scans every site you visit, blends Google Safe Browsing, Claude AI, and forensic heuristics, and flags malicious pages before you click.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/install" className="brutal-btn" data-testid="cta-install">
                Install Extension <ArrowRight size={16} />
              </Link>
              <Link to="/my" className="brutal-btn brutal-btn-outline" data-testid="cta-dashboard">
                My Dashboard
              </Link>
            </div>

            {/* Quick scan */}
            <form onSubmit={onScan} className="mt-10 brutal-card p-2 flex items-center gap-2 max-w-2xl" data-testid="quick-scan-form">
              <Search size={18} className="ml-2"/>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="paste any URL — e.g. secure-paypa1.com/login"
                className="flex-1 px-2 py-3 outline-none bg-transparent font-mono text-sm"
                data-testid="quick-scan-input"
              />
              <button type="submit" className="brutal-btn" disabled={loading} data-testid="quick-scan-btn">
                {loading ? "Scanning…" : "Scan"}
              </button>
            </form>
            {loading && <div className="scan-stripes h-2 mt-3 max-w-2xl" />}
            {error && <div className="mt-3 text-sm text-[#E53935] font-bold uppercase">{error}</div>}

            {result && (
              <div className="mt-8 brutal-card p-6 max-w-2xl flex gap-6 items-center" data-testid="quick-scan-result">
                <VerdictMark verdict={result.verdict} risk={result.risk} size={100} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Host</div>
                  <div className="font-bold truncate">{result.host}</div>
                  <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500">Reason</div>
                  <div className="text-sm">{result.ai_reason || (result.findings[0]?.detail) || "No issues detected"}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {result.categories.map((c) => (
                      <span key={c} className="text-[10px] border-2 border-black px-2 py-0.5 uppercase tracking-widest">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-5 flex items-center justify-center">
            <div className="brutal-card p-8 w-full">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Sample Verdict</div>
              <div className="mt-6 flex flex-col items-center gap-6">
                <VerdictMark verdict="safe" risk={4} size={180} />
              </div>
              <div className="mt-6 grid grid-cols-3 border-2 border-black">
                <div className="p-3 border-r-2 border-black">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Safe</div>
                  <div className="text-lg font-black">■</div>
                </div>
                <div className="p-3 border-r-2 border-black">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Caution</div>
                  <div className="text-lg font-black">▲</div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Danger</div>
                  <div className="text-lg font-black">⊗</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid md:grid-cols-3 gap-0 border-2 border-black bg-white">
          {[
            { icon: Shield, title: "Triple-Layer Defence", body: "Google Safe Browsing + Claude AI reasoning + forensic heuristics. Cross-validated on every scan." },
            { icon: Zap, title: "Real-Time Verdicts", body: "Tab-level detection runs in the background. The popup turns green, yellow, or red the moment a page loads." },
            { icon: Activity, title: "Audit Dashboard", body: "Every scan stored, charted, and queryable. See what was blocked, when, and why." },
          ].map((f, i) => (
            <div key={i} className={`p-8 ${i < 2 ? "border-r-2 border-black" : ""}`}>
              <div className="w-10 h-10 bg-black text-white flex items-center justify-center mb-4"><f.icon size={18} /></div>
              <div className="text-2xl font-black uppercase tracking-tight" style={{ fontFamily: "'Clash Display'" }}>{f.title}</div>
              <p className="mt-3 text-sm text-neutral-700">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="py-10 text-center text-xs tracking-[0.3em] uppercase text-neutral-500">
        WallGard · Built for the paranoid · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
